import { useState, useEffect } from 'react';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import type { PlaylistItem, Source } from '@jwp/ott-common/types/playlist';
import { getSources } from '@jwp/ott-common/src/utils/sources';
import { useAccessStore } from '@jwp/ott-common/src/stores/AccessStore';
import { useAppConfig } from './useAppConfig';
import { CONTENT_TYPE } from '@jwp/ott-common/src/constants';

export const useMediaSources = ({ item, baseUrl }: { item: PlaylistItem; baseUrl: string }) => {
  const config = useConfigStore((s) => s.config);
  const user = useAccountStore((s) => s.user);
  const passport = useAccessStore((s) => s.passport);
  const { featuredAdDeliveryMethod } = useAppConfig();
  const [sources, setSources] = useState<Source[]>([]);
  const [ssaiAdsData, setSsaiAdsData] = useState<any>([]);
  const effectiveAdDeliveryMethod = featuredAdDeliveryMethod || config?.adDeliveryMethod;

  const isLive = item?.contentType?.toLowerCase() == CONTENT_TYPE.liveChannel || item.contentType == CONTENT_TYPE.live;

  useEffect(() => {
    const fetchSourcesAndAds = async () => {
      const result = await getSources({ item, baseUrl, config, user, passport });
      setSources(result);

      if (result?.[0]?.file) {
        if (effectiveAdDeliveryMethod === 'ssai') {
          const data = await fetchSessionInfo(result[0].file, baseUrl, isLive);
          setSsaiAdsData(data);
        }
      }
    };

    fetchSourcesAndAds();
  }, [item, baseUrl, config, user, passport]);

  return { sources, ssaiAdsData };
};
function detectManifestType(manifestText: string): 'hls' | 'dash' {
  if (manifestText.includes('#EXTM3U')) { return 'hls'; }
  if (manifestText.includes('<MPD')) { return 'dash'; }
  throw new Error('Unknown manifest type');
}

const fetchSessionInfo = async (SSAIUrl: string, baseUrl: string, isLive: boolean) => {
  const options = { method: 'GET' };
  const res = await fetch(SSAIUrl, options);
  const manifestText = await res.text();
  const type = detectManifestType(manifestText);
  return parseSSAI(manifestText, SSAIUrl, type, isLive);
};

type SSAIInfo = {
  type: 'hls' | 'dash';
  isSSAI: boolean;
  isLive: boolean;

  prerollDuration: number;
  adBreakDuration: number;
  cuePoints: number[];
  midrolls: number[];

  renditions?: {
    width: number;
    height: number;
    bandwidth: number;
    url: string;
  }[];
};

export function parseSSAI(manifestText: string, manifestUrl: string, type: 'hls' | 'dash', isLive: boolean): SSAIInfo {
  if (type === 'hls') {
    return parseHlsSSAI(manifestText, manifestUrl, isLive);
  }

  if (type === 'dash') {
    return parseDashSSAI(manifestText, isLive);
  }

  throw new Error('Unsupported manifest type');
}

function parseHlsSSAI(manifestText: string, baseUrl: string, isLive: boolean): SSAIInfo {
  const lines = manifestText.split('\n');

  let isSSAI = false;
  let prerollDuration = 0;
  let adBreakDuration = 0;
  let cuePoints: number[] = [];

  const renditions = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    if (line.startsWith('#EXT-X-SESSION-DATA') && line.includes('com.jwpc.ssai-session-endpoint')) {
      isSSAI = true;
    }

    if (line.startsWith('#EXT-X-STREAM-INF')) {
      const bandwidth = Number(line.match(/BANDWIDTH=(\d+)/)?.[1] || 0);
      const res = line.match(/RESOLUTION=(\d+)x(\d+)/);

      const nextLine = lines[i + 1]?.trim();
      if (!nextLine || nextLine.startsWith('#')) { continue; }

      const url = new URL(nextLine, baseUrl).toString();
      renditions.push({
        bandwidth,
        width: res ? Number(res[1]) : 0,
        height: res ? Number(res[2]) : 0,
        url,
      });

      const params = new URL(url).searchParams;

      if (params.has('ad_break_duration')) {
        adBreakDuration = Number(params.get('ad_break_duration'));
      }

      if (params.has('ad_break_duration_preroll')) {
        prerollDuration = Number(params.get('ad_break_duration_preroll'));
      }

      const cuePointValue = params.get('cue_points');
      if (cuePointValue) {
        cuePoints = cuePointValue.split(',').map(Number);
      }
    }
  }

  return {
    type: 'hls',
    isSSAI,
    isLive,
    prerollDuration,
    adBreakDuration,
    cuePoints,
    midrolls: cuePoints.filter((t) => t > 0),
    renditions,
  };
}

function parseDashSSAI(mpdText: string, isLive: boolean): SSAIInfo {
  const parser = new DOMParser();
  const xml = parser.parseFromString(mpdText, 'application/xml');

  const periods = Array.from(xml.getElementsByTagName('Period'));

  let prerollDuration = 0;
  let cuePoints: number[] = [];
  if (periods.length) {
    // ✅ First Period = preroll (OTT rule)
    const preroll = periods[0];

    // Best case: Period duration
    const dur = preroll.getAttribute('duration');
    if (dur) {
      prerollDuration = parseISODuration(dur);
    } else {
      // Fallback: SegmentTimeline math
      prerollDuration = calculateDashPeriodDuration(preroll);
    }
  }

  // Cue points = Period start times
  let cursor = 0;
  for (const period of periods) {
    cuePoints.push(cursor);

    const dur = period.getAttribute('duration');
    if (dur) {
      cursor += parseISODuration(dur);
    }
  }

  return {
    type: 'dash',
    isSSAI: true,
    isLive,
    prerollDuration: Math.round(prerollDuration),
    adBreakDuration: Math.round(prerollDuration),
    cuePoints,
    midrolls: cuePoints.slice(1),
  };
}

function calculateDashPeriodDuration(period: Element): number {
  let duration = 0;

  const templates = Array.from(period.getElementsByTagName('SegmentTemplate'));

  for (const tmpl of templates) {
    const timescale = Number(tmpl.getAttribute('timescale')) || 1;
    const timeline = tmpl.getElementsByTagName('SegmentTimeline')[0];
    if (!timeline) { continue; }

    const segments = timeline.getElementsByTagName('S');
    for (const s of segments) {
      const d = Number(s.getAttribute('d'));
      const r = Number(s.getAttribute('r') || 0);
      duration += ((r + 1) * d) / timescale;
    }
  }

  return duration;
}

function parseISODuration(iso: string): number {
  const match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?/);

  if (!match) { return 0; }

  return Number(match[1] || 0) * 3600 + Number(match[2] || 0) * 60 + Number(match[3] || 0);
}
